<template>
    <aside id="floorplan-viewer">
        <transition name="fade">
            <div
                v-if="!isFloorplanViewOpen && isEnabled"
                @click="isFloorplanViewOpen = true"
                class="floorplan-view-btn d-flex justify-content-center align-items-center"
            >
                <icon name="ci-layer" color="white" />
            </div>
        </transition>

        <div class="floorplan-view-container" :class="{ 'floorplan-view-active': isFloorplanViewOpen && isEnabled }">
            <div class="header d-flex align-items-center">
                <SelectFloorplanInput
                    :allOptionEnabled="false"
                    :project="project"
                    :currentFloorplan="selectedFloorplan"
                    :showSectors="true"
                    :datesToFilter="selectedDatesArray"
                    v-model="floorplanInputValue"
                />
                <div class="dates-filter-container ml-2">
                    <ci-button
                        @click="isDatesFilterOpen = !isDatesFilterOpen"
                        :centerIcon="{ name: 'ci-calendar', color: selectedDatesArray && selectedDatesArray.length > 0 ? 'var(--secondary)' : 'white' }"
                        type="primary-1"
                        :class="{ 'filter-active': selectedDatesArray && selectedDatesArray.length > 0 }"
                    />
                    <div v-show="isDatesFilterOpen">
                        <ci-filter-date-select class="dates-filter" :dates="project.capture_dates" v-model="selectedDatesArray" />
                    </div>
                </div>
            </div>
            <div class="floorplan-img pr-2" :class="{ 'show-div-delay': isFloorplanViewOpen }">
                <FloorplanCanvas
                    v-if="isFloorplanViewOpen"
                    ref="zoomer"
                    :floorplan="selectedFloorplan.url || ''"
                    :viewpoints="selectedFloorplan.viewpoints"
                    :viewpointSize="getViewpointsSize()"
                    :grayscaleOnViewpointSelected="false"
                    :selectedViewpoint="currentViewpoint.id"
                    :enableViewpointNameTooltip="true"
                    :hoveredViewpoint="hoveredViewpoint.id"
                    :datesToFilter="selectedDatesArray"
                    @viewpointClick="viewpointClicked"
                    @viewpointHover="(vp) => (hoveredViewpoint = vp)"
                />
            </div>
            <div class="floorplan-controls pr-2">
                <div class="section-a">
                    <div @click="resetZoomer" class="control-btn d-flex justify-content-center align-items-center">
                        <icon name="arrows-alt-h" scale=".75" color="white" />
                    </div>
                    <div @click="zoomOut" class="control-btn d-flex justify-content-center align-items-center">
                        <icon name="search-minus" scale=".75" color="white" />
                    </div>
                </div>
                <div class="section-b">
                    <div @click="$emit('openFloorplanFullscreen')" class="fullscreen-btn d-flex justify-content-center align-items-center">
                        <icon name="expand" color="white" />
                    </div>
                </div>
                <div class="section-c">
                    <div @click="zoomIn" class="control-btn d-flex justify-content-center align-items-center">
                        <icon name="search-plus" scale=".75" color="white" />
                    </div>
                    <div @click="resizeViewpoints" class="control-btn d-flex justify-content-center align-items-center">
                        <icon v-if="getViewpointsSize() == 1" name="ci-resize-circle" color="white" scale=".75" />
                        <icon v-if="getViewpointsSize() == 2" name="ci-resize-circle-plus" color="white" scale="1.25" />
                        <icon v-if="getViewpointsSize() == 3" name="ci-resize-circle-plus-plus" color="white" scale="1.75" />
                    </div>
                </div>
            </div>
            <div
                @click="isFloorplanViewOpen = false"
                :class="{ 'show-div-delay': isFloorplanViewOpen }"
                class="close-btn d-flex justify-content-center align-items-center"
            >
                <icon name="chevron-left" color="white" scale=".5" />
            </div>
        </div>
    </aside>
</template>

<script>
// ====================
// UI Components
// ====================
import FloorplanCanvas from '@/components/FloorplanCanvas'
import SelectFloorplanInput from '@/components/360Viewer/Sidebar/Utils/SelectFloorplanInput'

// Vuex
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
    components: {
        FloorplanCanvas,
        SelectFloorplanInput,
    },
    props: {
        /**
         * determines if this component is open
         */
        isEnabled: {
            type: Boolean,
            default: false,
        },
        /**
         * the entire project
         */
        project: {
            type: Object,
        },
        /**
         * determines if project is a map or not
         */
        isMap: {
            type: Boolean,
        },
        /**
         * floorplan that user selected (navigation purposes)
         */
        selectedFloorplan: {
            default: -1,
        },
        /**
         * current viewpoint that is showing
         */
        currentViewpoint: {
            default: -1,
        },
    },
    data() {
        return {
            isFloorplanViewOpen: true,
            hoveredViewpoint: -1,
            floorplanInputValue: '',
            isDatesFilterOpen: false,
            selectedDatesArray: [],
        }
    },
    watch: {
        dateFilterViewer: {
            handler() {
                this.selectedDatesArray = this.getDatesByProjectId(this.project.id)
            },
            deep: true,
        },
        selectedDatesArray() {
            this.setDates({ projectId: this.project.id, dates: this.selectedDatesArray })
        },
        currentViewpoint() {
            let zoomer = this.$refs.zoomer

            if (zoomer) {
                zoomer.resetScale()
            }
        },
        floorplanInputValue: {
            handler() {
                let fpId = this.floorplanInputValue.replace('floorplan-', '')
                let fp = {}
                if (this.isMap) {
                    if (fpId == this.project.map.id) {
                        fp = {
                            id: this.project.map.id,
                            name: this.project.name,
                            url: this.project.map.url,
                            viewpoints: this.project.map.viewpoints,
                            sectors: this.project.map.sectors,
                        }
                    } else {
                        let sector = this.project.map.sectors.find((sec) => {
                            return sec.floorplans.find((fp) => {
                                return fp.id == fpId
                            })
                        })
                        if (sector) {
                            fp = sector.floorplans.find((fp) => {
                                return fp.id == fpId
                            })
                            return this.$emit('sectorChange', { fp, sector })
                        } else {
                            fp = this.project.floorplans.find((fp) => {
                                return fp.id == fpId
                            })
                        }
                    }
                    // fp = this.project.floorplans.find(fp => {
                    //     return fp.id == fpId
                    // })
                } else {
                    fp = this.project.floorplans.find((fp) => {
                        return fp.id == fpId
                    })
                }
                this.$emit('floorplanChange', fp)
            },
            deep: true,
        },
    },
    computed: {
        ...mapState(['dateFilterViewer']),
        ...mapGetters('dateFilterViewer', ['getDatesByProjectId']),
        ...mapGetters('viewpointsSize', ['getById']),
    },
    methods: {
        ...mapActions('viewpointsSize', ['addProject', 'addFloorplan']),
        ...mapActions('dateFilterViewer', ['setDates']),
        selectViewpoint(viewpoint) {
            this.$emit('viewpointClick', viewpoint)
        },
        // FloorplanCanvas events
        viewpointClicked(data) {
            let currentVp = null
            this.selectedFloorplan.viewpoints.forEach((vp) => {
                if (vp.id == data.id) {
                    currentVp = vp
                }
            })
            this.selectViewpoint(currentVp)
        },
        // CONTROLES DA FLOORPLAN
        resetZoomer() {
            let zoomer = this.$refs.zoomer || this.$refs['zoomer-fullscreen']
            zoomer.resetScale()
        },
        zoomIn() {
            let zoomer = this.$refs.zoomer || this.$refs['zoomer-fullscreen']
            zoomer.zoomIn()
        },
        zoomOut() {
            let zoomer = this.$refs.zoomer || this.$refs['zoomer-fullscreen']
            zoomer.zoomOut()
        },
        resizeViewpoints() {
            if (this.getViewpointsSize() < 3) {
                this.updateViewpointsSize(this.getViewpointsSize() + 1)
            } else {
                this.updateViewpointsSize(1)
            }
        },
        getViewpointsSize() {
            let projectSizeInfo = this.getById(this.project.id)

            if (projectSizeInfo) {
                if (this.isMap) {
                    if (this.selectedFloorplan.id == this.project.map.id) {
                        if (projectSizeInfo.mapSize) {
                            return projectSizeInfo.mapSize
                        }
                    } else if (projectSizeInfo.floorplans) {
                        let fp = projectSizeInfo.floorplans.find((fp) => fp.id == this.selectedFloorplan.id)
                        if (fp) {
                            return fp.size
                        } else {
                            this.updateViewpointsSize()
                        }
                    }
                } else if (projectSizeInfo.floorplans) {
                    let fp = projectSizeInfo.floorplans.find((fp) => fp.id == this.selectedFloorplan.id)
                    if (fp) {
                        return fp.size
                    } else {
                        this.updateViewpointsSize()
                    }
                }
            } else {
                this.updateViewpointsSize()
            }

            return 2
        },
        updateViewpointsSize(vpSize = 2) {
            if (this.isMap) {
                if (this.selectedFloorplan.id == this.project.map.id) {
                    this.addProject({ id: this.project.id, mapSize: vpSize })
                } else {
                    this.addFloorplan({ project_id: this.project.id, id: this.selectedFloorplan.id, size: vpSize })
                }
            } else {
                this.addFloorplan({ project_id: this.project.id, id: this.selectedFloorplan.id, size: vpSize })
            }
        },
    },
    mounted() {
        this.selectedDatesArray = this.getDatesByProjectId(this.project.id)
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/components/360Viewer/Sidebar/Floorplans/floorplan-viewer.scss';
</style>
